import { decodeBase64Url, decodeBase64UrlText } from './base64url';
import { IdpClaims } from './claims';
import { JwksResolver } from './jwks';
import { IdpAuthError } from './provider';

/**
 * Validacao do `id_token` no navegador, via JWKS (ADR 005 do `seazone-idp`).
 *
 * A SPA valida o ID token e nao o access token: o access e credencial para a
 * API do Sapron, que faz a propria dual validation; o que a SPA precisa provar
 * e a identidade que ela vai exibir e usar para decidir vinculo.
 */

const SEGMENTOS = 3;
const FOLGA_DE_RELOGIO_S = 30;
const MS_POR_SEGUNDO = 1000;

export interface DecodedJwt {
  readonly header: { readonly alg?: string; readonly kid?: string; readonly typ?: string };
  readonly payload: IdpClaims;
  readonly signingInput: string;
  readonly signature: Uint8Array;
}

/** Le um JWT bem formado **sem validar nada** — quem valida e `verifyIdToken`. */
export function decodeJwt(token: string): DecodedJwt {
  const parts = token.split('.');
  if (parts.length !== SEGMENTOS) {
    throw new IdpAuthError('jwt_malformado', 'Token do IDP nao tem tres segmentos.');
  }
  const [header, payload, signature] = parts;
  try {
    return {
      header: JSON.parse(decodeBase64UrlText(header)),
      payload: JSON.parse(decodeBase64UrlText(payload)),
      signingInput: `${header}.${payload}`,
      signature: decodeBase64Url(signature),
    };
  } catch (error) {
    throw new IdpAuthError('jwt_malformado', 'Token do IDP nao decodifica.', { cause: error });
  }
}

export interface VerifyIdTokenOptions {
  readonly issuer: string;
  readonly clientId: string;
  readonly nonce?: string;
  readonly getKey: JwksResolver;
  readonly now?: () => number;
  readonly clockToleranceSeconds?: number;
}

function audienceOk(claims: IdpClaims, clientId: string): boolean {
  const { aud, azp } = claims;
  if (typeof azp === 'string' && azp !== clientId) return false;
  if (typeof aud === 'string') return aud === clientId;
  if (Array.isArray(aud)) return aud.includes(clientId);
  // Sem `aud` o `azp` e quem identifica o client no contrato do IDP.
  return azp === clientId;
}

/**
 * Verifica assinatura, algoritmo, emissor, audiencia, `nonce` e expiracao —
 * nessa ordem, e todos obrigatorios.
 */
export async function verifyIdToken(
  token: string,
  options: VerifyIdTokenOptions,
): Promise<IdpClaims> {
  const {
    issuer,
    clientId,
    nonce,
    getKey,
    now = () => Date.now(),
    clockToleranceSeconds = FOLGA_DE_RELOGIO_S,
  } = options;

  const { header, payload, signingInput, signature } = decodeJwt(token);

  // Allowlist explicita de algoritmo. Sem ela, `alg: "HS256"` (ou `none`)
  // entrega a decisao de como verificar para quem assinou o token.
  if (header.alg !== 'RS256') {
    throw new IdpAuthError('jwt_alg_invalido', `Token do IDP com alg ${String(header.alg)}.`);
  }

  const key = await getKey(header.kid);
  const valid = await crypto.subtle.verify(
    'RSASSA-PKCS1-v1_5',
    key,
    signature,
    new TextEncoder().encode(signingInput),
  );
  if (!valid) {
    throw new IdpAuthError('jwt_assinatura_invalida', 'Assinatura do id_token nao confere.');
  }

  if (payload.iss !== issuer) {
    throw new IdpAuthError('jwt_iss_divergente', `id_token com iss ${String(payload.iss)}.`);
  }
  if (!audienceOk(payload, clientId)) {
    throw new IdpAuthError('jwt_aud_divergente', 'id_token emitido para outro client.');
  }
  if (nonce !== undefined && payload.nonce !== nonce) {
    throw new IdpAuthError('jwt_nonce_divergente', 'id_token nao responde a esta tentativa.');
  }
  if (typeof payload.exp !== 'number') {
    throw new IdpAuthError('claim_exp_ausente', 'id_token sem a claim `exp`.');
  }
  if (payload.exp + clockToleranceSeconds < Math.floor(now() / MS_POR_SEGUNDO)) {
    throw new IdpAuthError('jwt_expirado', 'id_token expirado.');
  }

  return payload;
}
